import React from 'react';
import { wrappedTranslations } from '../../translations/wrapped';
import { getClassificationColor } from '../../utils/classificationUtils';

interface ShareableImageProps {
    name: string;
    club: string;
    classification: string;
    totalGames: number;
    winPercentage: number;
    peakElo: number;
    bestStreak: number;
    lang: 'en' | 'fr' | 'de' | 'it';
}

/**
 * ShareableImage Component
 * This component renders an Instagram Story-sized image (1080x1920)
 * that summarizes the player's wrapped stats
 */
const ShareableImage = React.forwardRef<HTMLDivElement, ShareableImageProps>(
    ({ name, club, classification, totalGames, winPercentage, peakElo, bestStreak, lang }, ref) => {
        const t = wrappedTranslations[lang];
        const safeWinPercentage = isNaN(winPercentage) ? 0 : Math.min(100, winPercentage);
        const classificationColors = getClassificationColor(classification);

        return (
            <div
                ref={ref}
                className="relative bg-gradient-to-b from-gray-50 via-white to-gray-50 overflow-hidden"
                style={{
                    width: '1080px',
                    height: '1920px',
                    fontFamily: "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}
            >
                {/* Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    {/* Moving Orbs - Blue Theme */}
                    <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#1C7AD6]/8 rounded-full blur-[120px]" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-[#1C7AD6]/8 rounded-full blur-[120px]" />
                </div>

                {/* Content */}
                <div className="relative h-full flex flex-col items-center p-16 text-center">
                    {/* Header */}
                    <div className="flex flex-col items-center mb-12">
                        <img
                            src="/app-icon.png"
                            alt="TTStats"
                            className="h-28 w-28 mb-6 drop-shadow-[0_0_25px_rgba(28,122,214,0.4)]"
                        />

                        <h1 className="text-7xl font-bold mb-3 tracking-tight">
                            {['T', 'T', 'S', 't', 'a', 't', 's'].map((char, index) => (
                                <span
                                    key={index}
                                    className={index < 2 ? 'text-[#1C7AD6]' : 'text-gray-900'}
                                >
                                    {char}
                                </span>
                            ))}
                        </h1>

                        <p className="text-3xl font-light text-gray-600 tracking-widest uppercase">
                            {t.wrappedSubtitle}
                        </p>
                    </div>

                    {/* Player Info */}
                    <div className="w-full max-w-[900px] flex-1 flex flex-col justify-center">
                        <div className="bg-white rounded-3xl border-4 border-gray-200 shadow-2xl p-10 mb-10">
                            <p className="text-6xl font-bold text-gray-900 mb-4 break-words">
                                {name}
                            </p>
                            <p className="text-4xl text-[#1C7AD6] font-medium">
                                {club}
                            </p>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-6 mb-10">
                            <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                                <div className="text-xl text-gray-600 uppercase tracking-widest mb-2">{t.matchesPlayed}</div>
                                <div className="text-6xl font-bold text-gray-900">{totalGames}</div>
                            </div>
                            <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                                <div className="text-xl text-gray-600 uppercase tracking-widest mb-2">{t.winRate}</div>
                                <div className="text-6xl font-bold text-emerald-600">{safeWinPercentage.toFixed(0)}%</div>
                            </div>
                            <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                                <div className="text-xl text-gray-600 uppercase tracking-widest mb-2">{t.peakElo}</div>
                                <div className="text-6xl font-bold text-[#1C7AD6]">{peakElo}</div>
                            </div>
                            <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                                <div className="text-xl text-gray-600 uppercase tracking-widest mb-2">{t.matchWinStreak}</div>
                                <div className="text-6xl font-bold text-purple-600">{bestStreak}</div>
                            </div>
                        </div>

                        {/* Classification Badge */}
                        <div
                            className="inline-block px-10 py-5 text-white rounded-2xl text-4xl font-bold shadow-2xl"
                            style={{ backgroundColor: classificationColors.bg }}
                        >
                            {classification}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col items-center mt-12">
                        <div className="text-2xl text-gray-500 font-medium tracking-widest mb-3">
                            #TTStatsWrapped
                        </div>
                        <div className="text-xl text-gray-400">
                            ttstats.ch
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

ShareableImage.displayName = 'ShareableImage';

export default ShareableImage;
